// src/components/FormSelectionModal.jsx
import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  VStack,
  Spinner,
  Text,
  useToast,
  Progress,
  Box,
} from "@chakra-ui/react";
import { getSupabase } from "../components/supabaseClient";
import { BASE_URLS, tableName } from "../components/env";
import { processGeneratedFormResponse } from "../utils/formSubmissionUtils";
import { extractPatientInfo } from "../utils/fuzzyFormMapping";

const FormSelectionModal = ({
  isOpen,
  onClose,
  patientId,
  patientData,
  selectedLLMModel = 0,
}) => {
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [progress, setProgress] = useState(0);
  const [generatedForm, setGeneratedForm] = useState(null);
  const [sending, setSending] = useState(false);
  const toast = useToast();

  const handleGenerateForm = async () => {
    console.log("[FORM DEBUG] Starting form generation process");
    console.log("[FORM DEBUG] Patient data:", patientData);
    console.log("[FORM DEBUG] Patient ID:", patientId);

    if (!patientData) {
      toast({
        title: "Error",
        description: "Patient data is required to generate forms",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    setGenerating(true);
    setProgress(20);

    try {
      // Get current date for form generation
      const today = new Date();
      const year = today.getFullYear().toString();
      const date = today.toISOString().split("T")[0]; // YYYY-MM-DD format

      // Handle patient name extraction with better fallbacks
      let patientName = "";
      if (patientData.name) {
        patientName = patientData.name;
      } else if (patientData.LastName && patientData.FirstName) {
        patientName = `${patientData.LastName}, ${patientData.FirstName}`;
      } else if (patientData.LastName) {
        patientName = patientData.LastName;
      } else if (patientData.FirstName) {
        patientName = patientData.FirstName;
      } else {
        // Use fuzzy field mapping to extract name
        const extractedInfo = extractPatientInfo(patientData);
        patientName =
          extractedInfo.name ||
          `Patient ${patientId?.substring(0, 8) || "Unknown"}`;
      }

      console.log("[FORM DEBUG] Form generation parameters:", {
        uuid: patientId,
        patient_name: patientName,
        year: year,
        date: date,
        model: selectedLLMModel,
      });

      setProgress(40);

      // Call the backend API to generate form
      const hostname = window.location.hostname;
      const baseUrl =
        hostname === "localhost"
          ? BASE_URLS.local.backend
          : BASE_URLS.render.backend;

      console.log(
        "[FORM DEBUG] Making API call to:",
        `${baseUrl}/api/generate-form/`
      );

      const response = await fetch(`${baseUrl}/api/generate-form/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          uuid: patientId,
          patient_name: patientName,
          year: year,
          date: date,
          model: selectedLLMModel, // Use selected LLM model
        }),
      });

      console.log("[FORM DEBUG] API response status:", response.status);
      setProgress(60);

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to generate form");
      }

      const result = await response.json();
      console.log("[FORM DEBUG] API response data:", result);
      setProgress(80);

      if (result.success) {
        console.log("[FORM DEBUG] Form generation successful");
        console.log(
          "[FORM DEBUG] Generated form data keys:",
          Object.keys(result.form_data || {})
        );
        console.log(
          "[FORM DEBUG] Number of form items:",
          result.form_data?.items?.length || 0
        );

        // Store the complete form data including form_id and form_link for later use
        const completeFormData = {
          ...result.form_data,
          form_id:
            result.form_data?.form_id || result.form_data?.metadata?.form_id,
          form_link:
            result.form_data?.form_link ||
            result.form_data?.metadata?.form_link,
          local_path:
            result.form_data?.local_path ||
            result.form_data?.metadata?.local_path,
        };

        console.log("[FORM DEBUG] Complete form data:", completeFormData);
        setGeneratedForm(completeFormData);
        setProgress(100);

        toast({
          title: "Form Generated",
          description: "Patient form has been generated successfully",
          status: "success",
          duration: 5000,
          isClosable: true,
        });
      } else {
        console.log("[FORM DEBUG] Form generation failed:", result.error);
        throw new Error(result.error || "Unknown error occurred");
      }
    } catch (error) {
      console.error("Error generating form:", error);
      toast({
        title: "Generation Failed",
        description:
          error.message || "An error occurred while generating the form",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setGenerating(false);
      setProgress(0);
    }
  };

  const handleSendForm = async () => {
    if (!generatedForm) return;

    setSending(true);

    try {
      // Form is already generated and saved during handleGenerateForm
      // We just need to add it to the submissions_array column
      console.log(
        "[FORM SUBMISSION] Adding already generated form to submissions:"
      );

      // Create a result object that matches what processGeneratedFormResponse expects
      // The function looks for form_id at the top level or in form_data.metadata
      const resultData = {
        form_id: generatedForm.form_id,
        form_link: generatedForm.form_link,
        local_path: generatedForm.local_path,
        success: true,
        // Also include form_data structure for backward compatibility
        form_data: {
          metadata: {
            form_id: generatedForm.form_id,
            form_link: generatedForm.form_link,
            local_path: generatedForm.local_path,
            storage_path: generatedForm.local_path,
          },
        },
      };

      const addedToSubmissions = await processGeneratedFormResponse(
        patientId,
        resultData
      );

      if (addedToSubmissions) {
        toast({
          title: "Form Saved and Added to Submissions",
          description:
            "The generated form has been saved and added to the patient's submission column",
          status: "success",
          duration: 5000,
          isClosable: true,
        });
      } else {
        toast({
          title: "Form Saved",
          description:
            "The generated form has been saved to the patient record",
          status: "success",
          duration: 5000,
          isClosable: true,
        });
      }

      onClose();
    } catch (error) {
      console.error("Error saving form:", error);
      toast({
        title: "Save Failed",
        description: error.message || "Failed to save the generated form",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Generate Follow-up Form</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {generating ? (
            <VStack spacing={4}>
              <Text>Generating form...</Text>
              <Progress value={progress} width="100%" />
              <Text fontSize="sm" color="gray.600">
                {progress < 40 && "Initializing..."}
                {progress >= 40 &&
                  progress < 60 &&
                  "Processing patient data..."}
                {progress >= 60 && progress < 80 && "Generating questions..."}
                {progress >= 80 && progress < 100 && "Finalizing form..."}
                {progress >= 100 && "Complete!"}
              </Text>
            </VStack>
          ) : generatedForm ? (
            <VStack align="start" spacing={4}>
              <Text fontWeight="bold">Form Generated Successfully!</Text>
              <Text>
                A follow-up form has been generated for{" "}
                {patientData?.name || "the patient"}.
              </Text>
              <Box p={4} bg="gray.50" borderRadius="md" width="100%">
                <Text fontSize="sm" color="gray.600">
                  Form contains {generatedForm.items?.length || 0} questions
                </Text>
              </Box>
            </VStack>
          ) : (
            <VStack align="start" spacing={4}>
              <Text>
                Generate a personalized follow-up form for{" "}
                {patientData?.name || "the patient"}
                based on their clinical history.
              </Text>
              <Text fontSize="sm" color="gray.600">
                This will create a customized questionnaire using AI analysis of
                the patient's records.
              </Text>
            </VStack>
          )}
        </ModalBody>
        <ModalFooter>
          <Button
            variant="ghost"
            mr={3}
            onClick={onClose}
            isDisabled={generating}
          >
            Cancel
          </Button>
          {!generatedForm ? (
            <Button
              colorScheme="purple"
              onClick={handleGenerateForm}
              isLoading={generating}
              isDisabled={loading || !patientData}
            >
              Generate Form
            </Button>
          ) : (
            <Button
              colorScheme="teal"
              onClick={handleSendForm}
              isLoading={sending}
              loadingText="Saving..."
              isDisabled={loading || sending}
            >
              Save Form to Patient
            </Button>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default FormSelectionModal;
